// @ts-check
import React, { useState } from 'react';
import styled from 'styled-components';
import { createAuthHeaders } from 'core/services/auth-service';
import { encryptJson } from 'core/utils/encryption-utils';
import { API } from 'core/utils/http-utils';

const Container = styled.div`
  /* Use intrinsic sizing so we don't overflow the modal shell. */
  width: 100%;
  max-width: 640px; /* keep reasonable width, let parent center it */
  /* Allow the title to shrink slightly on narrow containers */
  h4 {
    white-space: nowrap;
    font-size: clamp(0.95rem, 2.2vw, 1.15rem);
  }

  .form-row {
    margin-bottom: 15px;
    display: flex;
    gap: 15px;
  }

  .form-group {
    flex: 1;
  }

  .form-control {
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }

  .credentials-link {
    color: #8c1d40;
    text-decoration: underline;
    cursor: pointer;
  }

  .export-button-disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .success-message {
    margin: 10px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

/**
 * @param {{
 *  onCancel: () => void,
 *  selectedFields: string[],
 *  campaignId: string
 * }} props
 */
const SalesforceCredentialsModal = ({
  onCancel,
  selectedFields,
  campaignId,
}) => {
  const [credentials, setCredentials] = useState({
    ftpSite: 'ftp.s7.exacttarget.com',
    port: '22',
    username: '',
    password: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      const credentialsPayload = {
        username: credentials.username,
        password: credentials.password,
      };

      const encryptedCredentials = await encryptJson(credentialsPayload);

      // Create auth headers
      const authHeaders = await createAuthHeaders();

      await API.post(
        `/campaign/${campaignId}/exports/sfmc`,
        encryptedCredentials,
        {
          headers: {
            ...authHeaders,
            'Content-Type': 'text/plain', // Since we're sending encrypted string directly
          },
          transformRequest: [(data) => data], // Prevent axios from JSON.stringify-ing the data
        },
      );

      setIsSubmitted(true);
    } catch (err) {
      let errorMessage =
        'An error occurred while submitting the export request';

      if (err.response) {
        const status = err.response.status;
        if (status === 400) {
          errorMessage = `Bad request: ${
            err.response.data?.message || 'Invalid request format'
          }`;
        } else if (status === 401) {
          errorMessage =
            'Authentication failed. Please check your credentials.';
        } else if (status === 403) {
          errorMessage = 'You do not have permission to export to SFMC.';
        } else if (status === 404) {
          errorMessage = 'Campaign not found.';
        } else if (status === 422) {
          errorMessage = 'Campaign has no finished statements to export.';
        }
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if form is complete
  const isFormComplete =
    credentials.ftpSite.trim() !== '' &&
    credentials.port.trim() !== '' &&
    credentials.username.trim() !== '' &&
    credentials.password.trim() !== '';

  return (
    <Container>
      <div className="d-flex justify-content-between mb-1 mt-1">
        <h4>Export to Enterprise Salesforce Marketing Cloud FTP</h4>
      </div>{' '}
      {!isSubmitted ? (
        <>
          <div className="mb-3">
            Please enter your department's FTP credentials below. If needed,
            refer to{' '}
            <a
              href="https://asu.service-now.com/sp?id=sc_category&sys_id=bc6611971bdcb8905201993f034bcb6c&catalog_id=e0d08b13c3330100c8b837659bba8fb4"
              target="_blank"
              rel="noreferrer"
            >
              this ServiceNow knowledge base
            </a>
            .
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ftpSite">FTP site</label>
              <input
                type="text"
                className="form-control"
                disabled
                id="ftpSite"
                name="ftpSite"
                placeholder="ftp.s7.exacttarget.com"
                value={credentials.ftpSite}
                onChange={handleChange}
              />
            </div>
            <div
              className="form-group"
              style={{ flexBasis: '100px', flexGrow: 0 }}
            >
              <label htmlFor="port">Port</label>
              <input
                type="text"
                className="form-control"
                disabled
                id="port"
                name="port"
                placeholder="22"
                value={credentials.port}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-group mb-3">
            <label htmlFor="username">FTP username</label>
            <input
              type="text"
              className="form-control"
              id="username"
              name="username"
              placeholder="Enter your username"
              value={credentials.username}
              onChange={handleChange}
            />
          </div>
          <div className="form-group mb-4">
            <label htmlFor="password">FTP password</label>
            <input
              type="password"
              className="form-control"
              id="password"
              name="password"
              placeholder="Enter your password"
              value={credentials.password}
              onChange={handleChange}
            />
          </div>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <div className="d-flex justify-content-between gap-2 mt-4">
            <button
              type="button"
              className="btn btn-md btn-gray"
              aria-describedby="cancel-export-list"
              disabled={isSubmitting}
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Salesforce Credentials Modal"
              data-ga-component=""
              data-ga-text="Cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className={`btn btn-md btn-maroon ${
                !isFormComplete || isSubmitting ? 'export-button-disabled' : ''
              }`}
              type="button"
              aria-describedby="submit-ftp-credentials"
              disabled={!isFormComplete || isSubmitting}
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Salesforce Credentials Modal"
              data-ga-component=""
              data-ga-text="Submit"
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="success-message text-center">
            <h5 className="mb-1">Request Submitted</h5>
            <p>A confirmation email will be sent to the creator.</p>
          </div>

          <div className="d-flex justify-content-center mt-4">
            <button
              type="button"
              className="btn btn-md btn-gray"
              aria-describedby="close-export-ftp"
              data-ga-event="link"
              data-ga-action="click"
              data-ga-name="onclick"
              data-ga-type="cta"
              data-ga-region="main content"
              data-ga-section="Salesforce Credentials Modal"
              data-ga-component=""
              data-ga-text="Close"
              onClick={onCancel}
            >
              Close
            </button>
          </div>
        </>
      )}
    </Container>
  );
};

export { SalesforceCredentialsModal };
